import { Box, TruncatedText } from "@nulogy/components";
import CollapsibleSection from "components/common/CollapsibleSection";
import Field from "components/common/Field";
import Section from "components/common/Section";
import {
  formatBoolean,
  formatDate,
  formatDateTime,
  formatPercentage,
  formatProductionRate,
  formatQuantity,
} from "components/helpers/format";
import { getWorkOrderPriorityLabel } from "components/pages/schedule/helpers/workOrderCardHelper";
import PackmanagerWorkOrderLink from "components/pages/schedule/PackmanagerWorkOrderLink";
import WorkOrder from "domain/WorkOrder";
import useApplicationContext from "hooks/apollo/applicationContext/useApplicationContext";
import upperFirst from "lodash/upperFirst";
import Codenames from "policies/Codenames";
import React from "react";
import EditNotes from "./EditNotes";
import ScheduledWorkBlockDetailsContainer from "./ScheduledWorkBlockDetailsContainer";

export default function WorkOrderDetailsView({ initialSectionOpenState, readonly, toggleSectionState, workOrder }) {
  const {
    code,
    customWorkOrderFieldSelections,
    customerName,
    description,
    workOrderPriority,
    due,
    externalId,
    externalUuid,
    id: workOrderId,
    itemCode,
    itemDescription,
    itemRecommendation,
    lineTypePreferencePrimary,
    lineTypePreferenceSecondary,
    notes,
    performance,
    producedQuantity,
    productionLastUpdatedAt,
    productionRateDependsOnNumberOfPeople,
    quantity,
    reference1,
    reference2,
    reference3,
    reference4,
    reference5,
    scheduledQuantity,
    standardPeople,
    standardProductionRate,
    status,
    unitOfMeasure,
    itemClass,
    itemFamily,
    itemType,
    itemCategory,
    serviceCategory,
    materialAvailabilityDate,
    startAt,
    endAt,
  } = workOrder;

  const applicationContext = useApplicationContext();

  if (applicationContext.loading) return null;

  const recommendedPeople = itemRecommendation?.recommendedPeople;
  const recommendedProductionRate = itemRecommendation?.recommendedProductionRate;

  const formattedProductionRate = formatProductionRate({ productionRate: standardProductionRate, unitOfMeasure });
  const formattedRecommendedProductionRate = formatProductionRate({
    productionRate: recommendedProductionRate,
    unitOfMeasure,
  });
  const formattedQuantity = formatQuantity(quantity);
  const formattedProducedQuantity = formatQuantity(producedQuantity);
  const formattedScheduledQuantity = WorkOrder.isFullyScheduled(workOrder)
    ? formattedQuantity
    : formatQuantity(scheduledQuantity);

  return (
    <Box data-testid="work-order-details-root" marginBottom="40px">
      <Section marginBottom="x5">
        <EditNotes notes={notes} readonly={readonly} workOrderId={workOrderId} />
      </Section>

      <CollapsibleSection
        className="spec-work-order-details-scheduled-blocks"
        isOpen={initialSectionOpenState.scheduledBlocks}
        onToggle={toggleSectionState("scheduledBlocks")}
        title="Scheduled blocks"
      >
        <ScheduledWorkBlockDetailsContainer workOrder={workOrder} />
      </CollapsibleSection>

      <CollapsibleSection
        isOpen={initialSectionOpenState.workOrderInformation}
        onToggle={toggleSectionState("workOrderInformation")}
        title="Work order details"
      >
        <Field labelText="Work order code">
          <PackmanagerWorkOrderLink
            code={
              <TruncatedText fullWidth maxWidth="350px">
                {code}
              </TruncatedText>
            }
            externalUuid={externalUuid}
          />
        </Field>
        <Field labelText="Customer">{customerName}</Field>
        <Field labelText="Due date">{formatDate(due)}</Field>
        <Field labelText="Item code">{itemCode}</Field>
        <Field labelText="Item description">{itemDescription}</Field>
        <Field labelText="Work order ID">{externalId}</Field>
        <Field labelText="Work order description">{description}</Field>
        <Field labelText="Work order status">{upperFirst(status)}</Field>
        <Field labelText="Work order priority">{getWorkOrderPriorityLabel(workOrderPriority)}</Field>
        <Field labelText="Material availability date">{formatDate(materialAvailabilityDate)}</Field>
        <Field labelText={`Planned start (from ${Codenames.PackmanagerShort})`}>{formatDate(startAt)}</Field>
        <Field labelText={`Planned end (from ${Codenames.PackmanagerShort})`}>{formatDate(endAt)}</Field>
        {customWorkOrderFieldSelections.map((selection, index) => {
          return (
            <Field
              data-testid={`work-order-details-custom-work-order-field-name-${index + 1}`}
              key={selection.id}
              labelText={selection.customWorkOrderField?.label}
            >
              {selection.customWorkOrderFieldValue?.description}
            </Field>
          );
        })}
        <Field labelText="Reference 1">{reference1}</Field>
        <Field labelText="Reference 2">{reference2}</Field>
        <Field labelText="Reference 3">{reference3}</Field>
        <Field labelText="Reference 4">{reference4}</Field>
        <Field labelText="Reference 5">{reference5}</Field>
      </CollapsibleSection>

      <CollapsibleSection
        isOpen={initialSectionOpenState.productionInformation}
        onToggle={toggleSectionState("productionInformation")}
        title="Production details"
      >
        <Field labelText="Last production recorded at">{formatDateTime(productionLastUpdatedAt)}</Field>
        <Field labelText="Quantity expected">{`${quantity} ${unitOfMeasure}`}</Field>
        <Field labelText="Quantity scheduled">
          {formattedScheduledQuantity} {unitOfMeasure}
        </Field>
        <Field labelText="Quantity produced">
          {formattedProducedQuantity} {unitOfMeasure}
        </Field>
        <Field labelText="Performance">{formatPercentage(performance)}</Field>
        <Field labelText="Standard production rate">{formattedProductionRate}</Field>
        <Field labelText="Recommended production rate">{formattedRecommendedProductionRate}</Field>
        <Field labelText="Standard people">{formatQuantity(standardPeople)}</Field>
        <Field labelText="Recommended people">{formatQuantity(recommendedPeople)}</Field>
        <Field labelText="Production rate depends on number of people">
          {formatBoolean(productionRateDependsOnNumberOfPeople)}
        </Field>
      </CollapsibleSection>

      <CollapsibleSection
        isOpen={initialSectionOpenState.itemDetails}
        onToggle={toggleSectionState("itemDetails")}
        title="Item details"
      >
        <Field labelText="Primary line type preference">{lineTypePreferencePrimary}</Field>
        <Field labelText="Secondary line type preference">{lineTypePreferenceSecondary}</Field>
        <Field labelText="Item class">{itemClass?.name}</Field>
        <Field labelText="Item family">{itemFamily?.name}</Field>
        <Field labelText="Item type">{itemType?.name}</Field>
        <Field labelText="Item category">{itemCategory?.name}</Field>
        <Field labelText="Service category">{serviceCategory?.name}</Field>
      </CollapsibleSection>
    </Box>
  );
}
